import { useState, useMemo, useEffect } from 'react';
import { showSuccessMessage } from '../../../../../utils';
import { UserProfession } from '../../../../../enums/UserProfession';
import { useCreateOnsiteInterviews } from '../../../../../domain/PermanentJobs';

// Default values
const DEFAULT_HOURLY_RATE = '42';
const DEFAULT_START_TIME = '08:00';
const DEFAULT_END_TIME = '12:00';
const DEFAULT_PROFESSION = 'RDH';
const DEFAULT_SPECIALTY = 'specialty_General Dentistry';

/**
 * Initial form state
 */
const initialFormState = {
  profession: DEFAULT_PROFESSION,
  subProfession: null,
  specialty: DEFAULT_SPECIALTY,
  localStart: DEFAULT_START_TIME,
  localEnd: DEFAULT_END_TIME,
  hourlyRate: DEFAULT_HOURLY_RATE,
  breakTime: '',
  isBreakPaid: false,
};

/**
 * @param {Object} params
 * @param {import('../../../../../domain/PermanentJobs/types/PostingDetails').IPostingDetailsEntity} params.posting
 */
export const useOnsiteInterviewForm = ({ posting }) => {
  const [formValues, setFormValues] = useState(initialFormState);

  const mutation = useCreateOnsiteInterviews();

  // Calendar and time state
  const [selectedDates, setSelectedDates] = useState({});

  useEffect(() => {
    if (posting) {
      setFormValues({
        ...initialFormState,
        profession: posting?.professionType,
        subProfession: posting?.subProfession,
        specialty: posting?.specialty,
      });
    }
  }, [posting]);

  const handleChange = (field, value) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleProfessionChange = (professionCode) => {
    setFormValues((prev) => ({
      ...prev,
      profession: professionCode,
      // Reset sub-profession if profession is not DA
      subProfession: professionCode === UserProfession.DA ? prev.subProfession : null,
    }));
  };

  const handleBreakChange = (breakTime, isBreakPaid) => {
    setFormValues((prev) => ({
      ...prev,
      breakTime,
      isBreakPaid,
    }));
  };

  /**
   * Validation logic - computed on every render
   * Returns object with individual field errors and overall form validity
   */
  const errorStatus = useMemo(() => {
    const missingPostingId = !posting;
    const invalidProfession = !formValues.profession;
    const invalidSpecialty = !formValues.specialty;
    const invalidMealBreak = !formValues.breakTime;

    const invalidRate =
      !formValues.hourlyRate ||
      Number.isNaN(Number(formValues.hourlyRate)) ||
      parseFloat(formValues.hourlyRate) <= 0;

    const isEmptyDate = Object.keys(selectedDates).length === 0;

    const invalidForm =
      missingPostingId ||
      invalidProfession ||
      invalidSpecialty ||
      invalidRate ||
      invalidMealBreak ||
      isEmptyDate;

    return {
      invalidForm,
      missingPostingId,
      invalidProfession,
      invalidSpecialty,
      invalidRate,
      invalidMealBreak,
    };
  }, [posting, formValues, selectedDates]);

  const mapFormValuesToPayload = useMemo(() => {
    const dates = Object.keys(selectedDates);

    return dates.map((date) => ({
      localStart: formValues.localStart,
      localEnd: formValues.localEnd,
      localDate: date,
      rate: parseFloat(formValues.hourlyRate),
      breakTime: parseInt(formValues.breakTime, 10),
      isBreakPaid: formValues.isBreakPaid,
      profession: formValues.profession,
      subProfession: formValues.subProfession,
      specialty: formValues.specialty,
    }));
  }, [formValues, selectedDates]);

  /**
   * Submit handler that constructs the payload and calls the mutation
   */
  const handleSubmit = (callback) => {
    mutation.mutate(
      {
        postingId: posting?.id,
        onsiteInterviews: mapFormValuesToPayload,
        duplicate: true,
      },
      {
        onSuccess: () => {
          showSuccessMessage({ message: 'Onsite interviews created successfully!' });

          // Reset form to initial state
          setFormValues(initialFormState);
          setSelectedDates({});

          callback?.();
        },
      },
    );
  };

  return {
    formValues,
    setFormValues,
    handleChange,
    handleProfessionChange,
    handleBreakChange,
    handleSubmit,
    errorStatus,
    selectedDates,
    setSelectedDates,
    previewData: mapFormValuesToPayload,
    isSubmitting: mutation.isLoading,
  };
};
